import _extends from '@babel/runtime/helpers/esm/extends';
import { off, on, preventDefault } from '../../utils/dom/event';
import SortIcon from './sort-icon';
import Icon from '../icon';
import EmptyContent, { emptyContentProps } from './empty-content';
import TableIndicator from './indicator';
import { computeColumnWidth, transformWidth } from './algorithm';
import { createNamespace, isDef } from '../../utils';
import { addClass } from '../../utils/create/bem';
import { TouchMixin } from '../../mixins/touch';

var _createNamespace = createNamespace('table'),
  createComponent = _createNamespace[0];

var _createNamespace2 = createNamespace('table-wrapper'),
  bem = _createNamespace2[1];

var _createNamespace3 = createNamespace('table-track'),
  trackBem = _createNamespace3[1];

var _createNamespace4 = createNamespace('table'),
  tableBem = _createNamespace4[1];

var _createNamespace5 = createNamespace('thead'),
  theadBem = _createNamespace5[1];

var _createNamespace6 = createNamespace('tbody'),
  tbodyBem = _createNamespace6[1];

var _createNamespace7 = createNamespace('tfoot'),
  tfootBem = _createNamespace7[1];

var _createNamespace8 = createNamespace('tr'),
  trBem = _createNamespace8[1];

var _createNamespace9 = createNamespace('th'),
  thBem = _createNamespace9[1];

var _createNamespace10 = createNamespace('td'),
  tdBem = _createNamespace10[1];

var DEFAULT_EXPAND_COLUMN_WIDTH = 40;
var SPEED_LIMIT = 0.2;
export default createComponent({
  props: _extends({}, emptyContentProps, {
    columns: {
      type: Array,
      default: function _default() {
        return [];
      }
    },
    data: {
      type: Array,
      default: function _default() {
        return [];
      }
    },
    dataPropsMapping: {
      type: Object,
      default: function _default() {
        return {
          id: 'id',
          children: 'children'
        };
      }
    },
    height: {
      type: [Number, String]
    },
    sortDataProp: {
      type: String
    },
    sortType: {
      type: String
    },
    expanded: {
      type: Array
    },
    expandOptions: {
      type: Object
    },
    ceilingOptions: {
      type: Object
    },
    loading: {
      type: Boolean
    },
    swipeable: {
      type: Boolean
    },
    showIndicator: {
      type: Boolean,
      default: true
    }
  }),
  data: function data() {
    return {
      columnStickyOffset: {},
      columnComputedWidth: {},
      showShadowLeft: false,
      showShadowRight: false,
      leftFixedBoundaryColumnIndex: -1,
      rightFixedBoundaryColumnIndex: -1,
      indicatorValue: [],
      moving: false,
      touchMoving: false,
      touchHorizontalMoving: false,
      transformIndex: 0,
      touchDistance: 0,
      transformGap: 0,
      columnTransformOffset: 0,
      rightColumnTransformOffset: 0
    };
  },
  mixins: [TouchMixin],
  created: function created() {
    var columns = this.columns;
    var firstColumnUnFixed = columns.find(function (column) {
      return column.fixed !== 'left';
    });
    this.transformIndex = firstColumnUnFixed
      ? columns.indexOf(firstColumnUnFixed)
      : 0;
  },
  mounted: function mounted() {
    var _this = this;

    this.$watch(
      function () {
        return [_this.column, _this.expandOptions, _this.columnComputedWidth];
      },
      function () {
        requestAnimationFrame(function () {
          if (_this.$refs.tableFakeTr) {
            var expandOptions = _this.expandOptions,
              columns = _this.columns; // TODO
            // 通过不可见行，获取到每列的 offsetLeft
            // 当前先通过 thead 来获取

            var fakeTdList = _this.$refs.tableFakeTr.querySelectorAll('td');

            var newColumnStickyOffset = {};
            var fakeTrWidth = _this.$refs.tableFakeTr.offsetWidth;
            var syntheticColumns = (
              expandOptions ? [expandOptions] : []
            ).concat(columns);
            syntheticColumns.forEach(function (column, index) {
              if (fakeTdList[index]) {
                var tdOffsetLeft = fakeTdList[index].offsetLeft;
                var tdOffsetWidth = fakeTdList[index].offsetWidth;
                newColumnStickyOffset[column.dataProp] = {
                  left: tdOffsetLeft,
                  right: fakeTrWidth - tdOffsetLeft - tdOffsetWidth,
                  width: tdOffsetWidth
                };
              }
            });
            _this.columnStickyOffset = newColumnStickyOffset; // calc shadow position

            var newLeftFixedBoundaryColumnIndex = -1;
            var newRightFixedBoundaryColumnIndex = -1;
            columns.forEach(function (column, index) {
              if (column.fixed === 'left') {
                newLeftFixedBoundaryColumnIndex = index;
              }

              if (
                newRightFixedBoundaryColumnIndex === -1 &&
                column.fixed === 'right'
              ) {
                newRightFixedBoundaryColumnIndex = index;
              }
            });

            if (newLeftFixedBoundaryColumnIndex < columns.length - 1) {
              _this.leftFixedBoundaryColumnIndex =
                newLeftFixedBoundaryColumnIndex;
            }

            if (newRightFixedBoundaryColumnIndex !== 0) {
              _this.rightFixedBoundaryColumnIndex =
                newRightFixedBoundaryColumnIndex;
            }
          }

          if (_this.swipeable) {
            // transform shadow
            _this.onContainerTransform();
          } else if (_this.$refs.track) {
            // scroll shadow
            _this.onContainerScroll(_this.$refs.track);
          }
        });
      },
      {
        immediate: true
      }
    );
    this.$watch(
      function () {
        return [_this.columns, _this.expandOptions];
      },
      function () {
        if (_this.$refs.wrapper) {
          var columns = _this.columns;
          _this.columnComputedWidth = computeColumnWidth(
            []
              .concat(
                _this.expandOptions
                  ? _extends(
                      {
                        width: DEFAULT_EXPAND_COLUMN_WIDTH
                      },
                      _this.expandOptions
                    )
                  : []
              )
              .concat(columns),
            _this.$refs.wrapper.offsetWidth
          );
        }
      }
    );
    this.$watch(
      function () {
        return [_this.ceilingOptions, _this.swipeable];
      },
      function () {
        var ceilingOptions = _this.ceilingOptions,
          swipeable = _this.swipeable;
        var scrollBoundary = _this.$refs.track;

        if (ceilingOptions) {
          scrollBoundary = ceilingOptions.scrollBoundary;
        }

        var onScroll = function onScroll() {
          _this.onContainerScroll(scrollBoundary);
        };

        if (!swipeable) {
          if (scrollBoundary === document) {
            on(scrollBoundary, 'scroll', onScroll);
          } else if (scrollBoundary) {
            on(scrollBoundary, 'scroll', onScroll);
          }
        }

        return function () {
          if (!swipeable) {
            if (scrollBoundary === document) {
              off(scrollBoundary, 'scroll', onScroll);
            } else if (scrollBoundary) {
              off(scrollBoundary, 'scroll', onScroll);
            }
          }
        };
      },
      {
        immediate: true
      }
    );
    this.$watch(
      function () {
        var columns = _this.columns,
          columnStickyOffset = _this.columnStickyOffset,
          columnTransformOffset = _this.columnTransformOffset,
          leftFixedBoundaryColumnIndex = _this.leftFixedBoundaryColumnIndex;
        return [
          columns,
          columnStickyOffset,
          columnTransformOffset,
          leftFixedBoundaryColumnIndex
        ];
      },
      function () {
        var value = [];

        if (!_this.$refs.wrapper) {
          return;
        }

        var leftFixedBoundaryColumnIndex = _this.leftFixedBoundaryColumnIndex,
          columnStickyOffset = _this.columnStickyOffset,
          columns = _this.columns,
          columnTransformOffset = _this.columnTransformOffset;
        var wrapperWidth = _this.$refs.wrapper.offsetWidth;
        var boundaryOffsetLeft = 0;

        if (leftFixedBoundaryColumnIndex !== -1) {
          var boundaryOffset =
            columnStickyOffset[columns[leftFixedBoundaryColumnIndex].dataProp];

          if (boundaryOffset) {
            boundaryOffsetLeft = boundaryOffset.left + boundaryOffset.width;
          }
        }

        columns.forEach(function (column, index) {
          if (column.fixed) {
            value.push(index);
            return;
          }

          var columnOffset = columnStickyOffset[column.dataProp];
          if (!columnOffset) return;

          if (
            columnOffset.left + columnTransformOffset < wrapperWidth &&
            columnOffset.left + columnOffset.width + columnTransformOffset >
              boundaryOffsetLeft
          ) {
            value.push(index);
          }
        });
        _this.indicatorValue = value;
      },
      {
        immediate: true
      }
    );
    this.$watch(
      function () {
        var columnStickyOffset = _this.columnStickyOffset,
          columns = _this.columns,
          transformIndex = _this.transformIndex,
          leftFixedBoundaryColumnIndex = _this.leftFixedBoundaryColumnIndex,
          touchDistance = _this.touchDistance;
        return [
          columnStickyOffset,
          columns,
          transformIndex,
          leftFixedBoundaryColumnIndex,
          touchDistance
        ];
      },
      function () {
        var columnStickyOffset = _this.columnStickyOffset,
          columns = _this.columns,
          transformIndex = _this.transformIndex,
          leftFixedBoundaryColumnIndex = _this.leftFixedBoundaryColumnIndex,
          touchDistance = _this.touchDistance;
        var columnOffset = columnStickyOffset[columns[transformIndex].dataProp];

        if (columnOffset) {
          var computedTransformOffset;

          if (leftFixedBoundaryColumnIndex !== -1) {
            var boundaryOffset =
              columnStickyOffset[
                columns[leftFixedBoundaryColumnIndex].dataProp
              ];
            computedTransformOffset =
              -(
                columnOffset.left -
                boundaryOffset.left -
                boundaryOffset.width
              ) + touchDistance;
          } else {
            computedTransformOffset = -columnOffset.left + touchDistance;
          }

          var wrapperWidth = _this.$refs.wrapper.offsetWidth;
          var scrollWidth = _this.$refs.table.offsetWidth;
          _this.columnTransformOffset = Math.max(
            computedTransformOffset,
            -(scrollWidth - wrapperWidth)
          );
          return;
        }

        _this.columnTransformOffset = touchDistance;
      },
      {
        immediate: true
      }
    );
    this.$watch(
      'columnTransformOffset',
      function () {
        if (_this.$refs.wrapper && _this.$refs.table) {
          var wrapperWidth = _this.$refs.wrapper.offsetWidth;
          var scrollWidth = _this.$refs.table.offsetWidth;
          _this.rightColumnTransformOffset =
            scrollWidth - wrapperWidth + _this.columnTransformOffset;
          return;
        }

        _this.rightColumnTransformOffset = 0;
      },
      {
        immediate: true
      }
    );
    this.bindTouchEvent(this.$refs.wrapper);
  },
  render: function render() {
    var h = arguments[0];
    var data = this.data,
      swipeable = this.swipeable,
      touchHorizontalMoving = this.touchHorizontalMoving,
      ceilingOptions = this.ceilingOptions,
      height = this.height;
    var isEmpty = !data.length;
    var classModify = {
      swipeable: swipeable,
      'swipeable-touching': touchHorizontalMoving,
      empty: isEmpty
    };

    if (ceilingOptions) {
      classModify['ceiling'] = true;
    }

    var className = bem([
      {
        empty: isEmpty
      }
    ]);
    var style = {};

    if (!data.length && !!height) {
      style.height = typeof height === 'number' ? height + 'px' : height;
    }

    return h(
      'div',
      {
        ref: 'wrapper',
        class: className,
        style: style
      },
      [
        this.TalbeSwipeableHeader,
        h(
          'div',
          {
            ref: 'track',
            class: trackBem([classModify]),
            style: this.tableStyle
          },
          [this.renderTable()]
        ),
        this.TableEmtpyBody
      ]
    );
  },
  methods: {
    onContainerScroll: function onContainerScroll(container) {
      var showShadowLeft = this.showShadowLeft,
        showShadowRight = this.showShadowRight;
      var scrollingElement =
        container === document ? document.documentElement : container;

      if (
        (scrollingElement.scrollLeft > 0 && !showShadowLeft) ||
        (scrollingElement.scrollLeft === 0 && showShadowLeft)
      ) {
        this.showShadowLeft = !showShadowLeft;
      }

      if (
        (scrollingElement.scrollLeft + scrollingElement.offsetWidth <
          scrollingElement.scrollWidth &&
          !showShadowRight) ||
        (scrollingElement.scrollLeft + scrollingElement.offsetWidth >=
          scrollingElement.scrollWidth &&
          showShadowRight)
      ) {
        this.showShadowRight = !showShadowRight;
      }
    },
    computeTransitionDistance: function computeTransitionDistance(
      lastTransformIndex,
      newTransformIndex
    ) {
      var columnStickyOffset = this.columnStickyOffset,
        columns = this.columns,
        leftFixedBoundaryColumnIndex = this.leftFixedBoundaryColumnIndex;
      var lastColumnOffset =
        columnStickyOffset[columns[lastTransformIndex].dataProp];
      var newColumnOffset =
        columnStickyOffset[columns[newTransformIndex].dataProp];

      if (lastColumnOffset && newColumnOffset) {
        var lastComputedTransformOffset;
        var newComputedTransformOffset;
        var wrapperWidth = this.$refs.wrapper.offsetWidth;
        var scrollWidth = this.$refs.table.offsetWidth;

        if (leftFixedBoundaryColumnIndex !== -1) {
          var boundaryOffset =
            columnStickyOffset[columns[leftFixedBoundaryColumnIndex].dataProp];
          lastComputedTransformOffset = -(
            lastColumnOffset.left -
            boundaryOffset.left -
            boundaryOffset.width
          );
          newComputedTransformOffset = -(
            newColumnOffset.left -
            boundaryOffset.left -
            boundaryOffset.width
          );
        } else {
          lastComputedTransformOffset = -lastColumnOffset.left;
          newComputedTransformOffset = -newColumnOffset.left;
        }

        return (
          Math.max(newComputedTransformOffset, -(scrollWidth - wrapperWidth)) -
          Math.max(lastComputedTransformOffset, -(scrollWidth - wrapperWidth))
        );
      }

      return 0;
    },
    onContainerTransform: function onContainerTransform() {
      var columnTransformOffset = this.columnTransformOffset,
        showShadowLeft = this.showShadowLeft,
        showShadowRight = this.showShadowRight;

      if (
        (columnTransformOffset < 0 && !showShadowLeft) ||
        (columnTransformOffset === 0 && showShadowLeft)
      ) {
        this.showShadowLeft = !showShadowLeft;
      }

      if (this.$refs.wrapper && this.$refs.table) {
        if (
          (this.$refs.wrapper.offsetWidth - columnTransformOffset <
            this.$refs.table.offsetWidth &&
            !showShadowRight) ||
          (this.$refs.wrapper.offsetWidth - columnTransformOffset >=
            this.$refs.table.offsetWidth &&
            showShadowRight)
        ) {
          this.showShadowRight = !showShadowRight;
        }
      }
    },
    onTouchStart: function onTouchStart(event) {
      if (!this.swipeable) {
        return;
      }

      this.touchStart(event);
      var moving = this.moving; // TODO
      // need to gurantee touches is accessible

      if (moving) {
        return;
      }

      if (event.touches.length === 1) {
        this.touchMoving = true;
      }
    },
    onTouchMove: function onTouchMove(event) {
      if (!this.swipeable) {
        return;
      }

      this.touchMove(event);
      var touchMoving = this.touchMoving,
        touchHorizontalMoving = this.touchHorizontalMoving,
        columnStickyOffset = this.columnStickyOffset,
        columns = this.columns,
        transformIndex = this.transformIndex,
        leftFixedBoundaryColumnIndex = this.leftFixedBoundaryColumnIndex,
        transformGap = this.transformGap; // TODO
      // need to gurantee states is not empty

      if (touchMoving) {
        var deltaX = this.deltaX,
          direction = this.direction;

        if (direction === 'horizontal') {
          preventDefault(event);
          !touchHorizontalMoving && (this.touchHorizontalMoving = true);
          var columnOffsetLeft =
            columnStickyOffset[columns[transformIndex].dataProp].left;
          var boundaryOffset =
            leftFixedBoundaryColumnIndex !== -1 &&
            columnStickyOffset[columns[leftFixedBoundaryColumnIndex].dataProp];
          var boundaryOffsetLeft = boundaryOffset
            ? boundaryOffset.left + boundaryOffset.width
            : 0;
          var wrapperWidth = this.$refs.wrapper.offsetWidth;
          var scrollWidth = this.$refs.table.offsetWidth;
          var truncDistance = Math.min(
            columnOffsetLeft - boundaryOffsetLeft,
            Math.max(
              deltaX + transformGap,
              -(
                scrollWidth -
                wrapperWidth -
                (columnOffsetLeft - boundaryOffsetLeft)
              )
            )
          );
          this.touchDistance = truncDistance;
        }
      }
    },
    onTouchEnd: function onTouchEnd() {
      if (!this.swipeable) {
        return;
      }

      var touchMoving = this.touchMoving,
        columnStickyOffset = this.columnStickyOffset,
        columns = this.columns,
        transformIndex = this.transformIndex,
        leftFixedBoundaryColumnIndex = this.leftFixedBoundaryColumnIndex,
        touchDistance = this.touchDistance;

      if (touchMoving) {
        this.touchMoving = false;
        this.touchHorizontalMoving = false;
        this.moving = true;
        this.touchDistance = 0; // compute speed

        var touchSpeed = 0;
        var speedX = this.speedX;

        if (Math.abs(speedX) > SPEED_LIMIT) {
          touchSpeed = speedX;
        }

        var columnOffsetLeft =
          columnStickyOffset[columns[transformIndex].dataProp].left;
        var wrapperWidth = this.$refs.wrapper.offsetWidth;
        var scrollWidth = this.$refs.table.offsetWidth;
        var boundaryOffset =
          leftFixedBoundaryColumnIndex !== -1 &&
          columnStickyOffset[columns[leftFixedBoundaryColumnIndex].dataProp];
        var boundaryOffsetLeft = boundaryOffset
          ? boundaryOffset.left + boundaryOffset.width
          : 0; // distance from right edge of table

        var orgEdgeDistance =
          scrollWidth -
          wrapperWidth -
          (columnOffsetLeft - boundaryOffsetLeft) +
          touchDistance;
        var hitEdge = orgEdgeDistance <= 0; // determine transform index

        var newTransformIndex = transformIndex;
        columns.some(function (column, columnIndex) {
          var _columnStickyOffset$c = columnStickyOffset[column.dataProp],
            left = _columnStickyOffset$c.left,
            width = _columnStickyOffset$c.width;
          var distanceFromCurrentColumn = left - columnOffsetLeft; // swipe to left

          if (
            columnIndex > transformIndex &&
            touchDistance < 0 &&
            touchDistance + distanceFromCurrentColumn > 0
          ) {
            if (touchDistance + distanceFromCurrentColumn - width / 2 > 0) {
              newTransformIndex = columnIndex;
            } else {
              newTransformIndex = columnIndex + 1;
            }

            if (hitEdge) {
              newTransformIndex = columnIndex + 1;
            }

            if (touchSpeed < 0) {
              newTransformIndex = columnIndex + 1;
            } else if (touchSpeed > 0) {
              newTransformIndex = columnIndex;
            }

            return true;
          } // swipe to right or swipe visible first column

          if (
            columnIndex <= transformIndex &&
            touchDistance + distanceFromCurrentColumn + width > 0
          ) {
            if (touchDistance + distanceFromCurrentColumn + width / 2 > 0) {
              newTransformIndex = columnIndex;
            } else {
              newTransformIndex = columnIndex + 1;
            }

            if (hitEdge) {
              newTransformIndex = columnIndex + 1;
            }

            if (touchSpeed < 0) {
              newTransformIndex = columnIndex + 1;
            } else if (touchSpeed > 0) {
              newTransformIndex = columnIndex;
            }

            return true;
          }

          return false;
        });
        newTransformIndex = Math.min(newTransformIndex, columns.length - 1);
        this.transformIndex = newTransformIndex;
        var finalColumnOffsetLeft =
          columnStickyOffset[columns[newTransformIndex].dataProp].left; // current transformed column has no space to swipe entirely
        // set the gap to its supposed to be

        var finalEdgeDistance =
          scrollWidth -
          wrapperWidth -
          (finalColumnOffsetLeft - boundaryOffsetLeft);

        if (finalEdgeDistance >= 0) {
          this.transformGap = 0;
        } else {
          this.transformGap = -finalEdgeDistance;
        } // handle condition transiton not triggered
        // not trigger transition

        var estimatedTouchDistance = this.computeTransitionDistance(
          transformIndex,
          newTransformIndex
        );

        if (
          estimatedTouchDistance === touchDistance || // exception condition: translate from right edge and finally landing on right edge
          touchDistance === this.transformGap
        ) {
          this.onTransitionEnd();
        }
      }
    },
    onTransitionEnd: function onTransitionEnd() {
      if (this.moving) {
        this.onContainerTransform();
        this.moving = false;
      }
    },
    appendTransitionToLeftFixedCell: function appendTransitionToLeftFixedCell(
      style
    ) {
      var swipeable = this.swipeable,
        columnTransformOffset = this.columnTransformOffset,
        moving = this.moving;

      if (!swipeable) {
        return;
      }

      style['transform'] =
        'translate3d(' + -columnTransformOffset + 'px, 0, 0)';

      if (moving) {
        style['transitionDuration'] = '0.3s';
      }
    },
    appendTransitionToRightFixedCell: function appendTransitionToRightFixedCell(
      style
    ) {
      var swipeable = this.swipeable,
        rightColumnTransformOffset = this.rightColumnTransformOffset,
        moving = this.moving;

      if (!swipeable) {
        return;
      }

      style['transform'] =
        'translate3d(' + -rightColumnTransformOffset + 'px, 0, 0)';

      if (moving) {
        style['transitionDuration'] = '0.3s';
      }
    },
    appendTransitionToFixedCell: function appendTransitionToFixedCell(
      style,
      fixedDirection
    ) {
      if (fixedDirection === 'left') {
        this.appendTransitionToLeftFixedCell(style);
      } else {
        this.appendTransitionToRightFixedCell(style);
      }
    },
    appendTransitionToTable: function appendTransitionToTable(style) {
      var swipeable = this.swipeable,
        columnTransformOffset = this.columnTransformOffset,
        moving = this.moving;

      if (!swipeable) {
        return;
      }

      style['transform'] = 'translate3d(' + columnTransformOffset + 'px, 0, 0)';

      if (moving) {
        style['transitionDuration'] = '0.3s';
      }
    },
    renderTable: function renderTable() {
      var h = this.$createElement;
      var tableStyle = {}; // unfixed column transform

      this.appendTransitionToTable(tableStyle);
      return h(
        'table',
        {
          ref: 'table',
          class: tableBem(),
          style: tableStyle,
          on: {
            transitionend: this.onTransitionEnd
          }
        },
        [this.ColumnGroup, this.TableHeader, this.TableBody, this.TableFooter]
      );
    }
  },
  computed: {
    tableStyle: function tableStyle() {
      var height = this.height;

      if (!height) {
        return {};
      }

      return {
        maxHeight: typeof height === 'number' ? height + 'px' : height
      };
    },
    TableHeader: function TableHeader() {
      var _this2 = this;

      var h = this.$createElement;
      var columns = this.columns,
        height = this.height,
        showShadowLeft = this.showShadowLeft,
        showShadowRight = this.showShadowRight,
        leftFixedBoundaryColumnIndex = this.leftFixedBoundaryColumnIndex,
        rightFixedBoundaryColumnIndex = this.rightFixedBoundaryColumnIndex,
        sortDataProp = this.sortDataProp,
        sortType = this.sortType,
        swipeable = this.swipeable,
        ceilingOptions = this.ceilingOptions,
        expandOptions = this.expandOptions,
        columnStickyOffset = this.columnStickyOffset;
      var isSticky = !!height || ceilingOptions;

      var renderExpandTh = function renderExpandTh() {
        var thStyle = {};
        var thClassModifiers = {
          sticky: isSticky
        };

        if (isSticky) {
          var stickyTop = (ceilingOptions && ceilingOptions.top) || 0;
          stickyTop =
            typeof stickyTop === 'number' ? stickyTop + 'px' : stickyTop;
          thStyle.top = stickyTop;
        }

        if (expandOptions.fixed) {
          thClassModifiers['sticky-' + expandOptions.fixed] = true;

          if (swipeable) {
            // unfixed column transform
            _this2.appendTransitionToFixedCell(thStyle, expandOptions.fixed);
          } else {
            var _columnStickyOffset$e;

            thStyle[expandOptions.fixed] =
              (((_columnStickyOffset$e =
                columnStickyOffset[expandOptions.dataProp]) == null
                ? void 0
                : _columnStickyOffset$e[expandOptions.fixed]) || 0) + 'px';
          }
        }

        var className = thBem([thClassModifiers]);

        if (expandOptions.cellClass) {
          className = addClass(className, expandOptions.cellClass);
        }

        if (expandOptions.thCellClass) {
          className = addClass(className, expandOptions.thCellClass);
        }

        return h('th', {
          key: expandOptions.dataProp,
          class: className,
          style: thStyle
        });
      };

      var tableHeaderContent = columns.map(function (column, columnIndex) {
        var sortable = column.sortable,
          filterable = column.filterable,
          dataProp = column.dataProp;
        var thStyle = {};
        var thClassModifiers = {
          sticky: isSticky
        };

        if (isSticky) {
          var stickyTop = (ceilingOptions && ceilingOptions.top) || 0;
          stickyTop =
            typeof stickyTop === 'number' ? stickyTop + 'px' : stickyTop;
          thStyle.top = stickyTop;
        }

        if (column.fixed) {
          thClassModifiers['sticky-' + column.fixed] = true;

          if (swipeable) {
            // unfixed column transform
            _this2.appendTransitionToFixedCell(thStyle, column.fixed);
          } else {
            var _columnStickyOffset$c2;

            thStyle[column.fixed] =
              (((_columnStickyOffset$c2 =
                columnStickyOffset[column.dataProp]) == null
                ? void 0
                : _columnStickyOffset$c2[column.fixed]) || 0) + 'px';
          }

          if (
            (showShadowLeft &&
              column.fixed === 'left' &&
              columnIndex === leftFixedBoundaryColumnIndex) ||
            (showShadowRight &&
              column.fixed === 'right' &&
              columnIndex === rightFixedBoundaryColumnIndex)
          ) {
            thClassModifiers['sticky-' + column.fixed + '-shadow'] = true;
          }
        }

        var renderSortIcon = function renderSortIcon() {
          var onSortIconClick = function onSortIconClick() {
            var newSortType;
            var newDataProp = column.dataProp;

            if (_this2.lastSortDataProp === newDataProp && sortType) {
              newSortType = sortType === 'asc' ? 'desc' : 'asc';
            } else {
              newSortType = 'desc';
            }

            _this2.lastSortDataProp = newDataProp;

            _this2.$emit('sort-change', column, newDataProp, newSortType);
          };

          return h(
            'div',
            {
              class: thBem('sort-icon'),
              on: {
                click: onSortIconClick
              }
            },
            [
              sortDataProp === dataProp
                ? h(SortIcon, {
                    attrs: {
                      sortType: sortType
                    }
                  })
                : h(SortIcon, {
                    attrs: {
                      sortType: undefined
                    }
                  })
            ]
          );
        };

        var renderFilterIcon = function renderFilterIcon() {
          var onFilterIconClick = function onFilterIconClick() {
            _this2.$emit('filter-change', column, column.dataProp);
          };

          return h(Icon, {
            class: thBem('filter-icon'),
            on: {
              click: onFilterIconClick
            },
            attrs: {
              name: 'filter'
            }
          });
        };

        var className = thBem([thClassModifiers]);

        if (column.cellClass) {
          className = addClass(className, column.cellClass);
        }

        if (column.thCellClass) {
          className = addClass(className, column.thCellClass);
        }

        return h(
          'th',
          {
            key: column.dataProp,
            class: className,
            style: thStyle
          },
          [
            column.renderThCell
              ? column.renderThCell(_this2.$createElement, {
                  column: column
                })
              : column.title,
            sortable && renderSortIcon(),
            filterable && renderFilterIcon()
          ]
        );
      });
      return h(
        'thead',
        {
          class: theadBem()
        },
        [
          h(
            'tr',
            {
              class: trBem()
            },
            [
              expandOptions &&
                expandOptions.fixed !== 'right' &&
                renderExpandTh(),
              tableHeaderContent,
              expandOptions &&
                expandOptions.fixed === 'right' &&
                renderExpandTh()
            ]
          )
        ]
      );
    },
    ColumnGroup: function ColumnGroup() {
      var h = this.$createElement;
      var columns = this.columns,
        columnComputedWidth = this.columnComputedWidth,
        expandOptions = this.expandOptions;

      var renderExpandCol = function renderExpandCol() {
        var width = expandOptions.width || DEFAULT_EXPAND_COLUMN_WIDTH + 'px';
        var colStyle = {
          width: width
        };
        return h('col', {
          style: colStyle
        });
      };

      var columnGroupContent = columns.map(function (column) {
        var colStyle = {};
        var colWidth =
          columnComputedWidth[column.dataProp] ||
          Math.max(
            isDef(column.width) ? transformWidth(column.width) : 0,
            isDef(column.minWidth) ? transformWidth(column.minWidth) : 0
          );
        colStyle.width =
          typeof colWidth === 'number' ? colWidth + 'px' : colWidth;
        return h('col', {
          key: column.dataProp,
          style: colStyle
        });
      });
      return h('colgroup', [
        expandOptions && expandOptions.fixed !== 'right' && renderExpandCol(),
        columnGroupContent,
        expandOptions && expandOptions.fixed === 'right' && renderExpandCol()
      ]);
    },
    TableBody: function TableBody() {
      var _this3 = this;

      var h = this.$createElement;
      var data = this.data,
        columns = this.columns,
        showShadowLeft = this.showShadowLeft,
        showShadowRight = this.showShadowRight,
        leftFixedBoundaryColumnIndex = this.leftFixedBoundaryColumnIndex,
        rightFixedBoundaryColumnIndex = this.rightFixedBoundaryColumnIndex,
        columnStickyOffset = this.columnStickyOffset,
        expanded = this.expanded,
        expandOptions = this.expandOptions,
        swipeable = this.swipeable;

      var renderExpandTd = function renderExpandTd(row, rowExpanded) {
        var tdStyle = {};
        var tdClassModifiers = {};

        if (expandOptions.fixed) {
          tdClassModifiers['sticky-' + expandOptions.fixed] = true;

          if (swipeable) {
            // unfixed column transform
            _this3.appendTransitionToFixedCell(tdStyle, expandOptions.fixed);
          } else {
            var _columnStickyOffset$e2;

            tdStyle[expandOptions.fixed] =
              (((_columnStickyOffset$e2 =
                columnStickyOffset[expandOptions.dataProp]) == null
                ? void 0
                : _columnStickyOffset$e2[expandOptions.fixed]) || 0) + 'px';
          }
        }

        var onExpandTdClick = function onExpandTdClick() {
          var newExpanded = [].concat(expanded);

          if (rowExpanded) {
            var expandIndex = expanded.indexOf(row[expandOptions.dataProp]);
            newExpanded.splice(expandIndex, 1);
          } else {
            newExpanded.push(row[expandOptions.dataProp]);
          }

          _this3.$emit('expand-change', row, newExpanded);
        };

        var className = tdBem([
          tdClassModifiers,
          {
            expanded: rowExpanded
          }
        ]);

        if (expandOptions.cellClass) {
          className = addClass(className, expandOptions.cellClass);
        }

        if (expandOptions.tdCellClass) {
          className = addClass(className, expandOptions.tdCellClass);
        }

        return h(
          'td',
          {
            key: expandOptions.dataProp,
            class: className,
            style: tdStyle,
            on: {
              click: onExpandTdClick
            }
          },
          [
            h(
              'div',
              {
                class: tdBem('expand')
              },
              [
                expandOptions.renderIcon
                  ? expandOptions.renderIcon(_this3.$createElement, {
                      rowData: row
                    })
                  : h(Icon, {
                      class: tdBem('expand-icon', [
                        {
                          expanded: rowExpanded
                        }
                      ]),
                      attrs: {
                        name: 'arrow-down'
                      }
                    })
              ]
            )
          ]
        );
      };

      var tableBodyContent = [];
      data.forEach(function (row, rowIndex) {
        var rowExpanded = expandOptions
          ? expanded.includes(row[expandOptions.dataProp])
          : false;
        var tableTrContent = columns.map(function (column, columnIndex) {
          var tdStyle = {};
          var tdClassModifiers = {};

          if (column.fixed) {
            tdClassModifiers['sticky-' + column.fixed] = true;

            if (swipeable) {
              // unfixed column transform
              _this3.appendTransitionToFixedCell(tdStyle, column.fixed);
            } else {
              var _columnStickyOffset$c3;

              tdStyle[column.fixed] =
                (((_columnStickyOffset$c3 =
                  columnStickyOffset[column.dataProp]) == null
                  ? void 0
                  : _columnStickyOffset$c3[column.fixed]) || 0) + 'px';
            }

            if (
              (showShadowLeft &&
                column.fixed === 'left' &&
                columnIndex === leftFixedBoundaryColumnIndex) ||
              (showShadowRight &&
                column.fixed === 'right' &&
                columnIndex === rightFixedBoundaryColumnIndex)
            ) {
              tdClassModifiers['sticky-' + column.fixed + '-shadow'] = true;
            }
          }

          var className = tdBem([
            tdClassModifiers,
            {
              expanded: rowExpanded
            }
          ]);

          if (column.cellClass) {
            className = addClass(className, column.cellClass);
          }

          if (column.tdCellClass) {
            className = addClass(className, column.tdCellClass);
          }

          return h(
            'td',
            {
              key: column.dataProp,
              class: className,
              style: tdStyle
            },
            [
              column.renderCell
                ? column.renderCell(_this3.$createElement, {
                    rowData: row,
                    column: column
                  })
                : row[column.dataProp]
            ]
          );
        });
        tableBodyContent.push(
          h(
            'tr',
            {
              key:
                'tr-' +
                (row[_this3.dataPropsMapping.id] !== undefined
                  ? row[_this3.dataPropsMapping.id]
                  : rowIndex),
              class: trBem()
            },
            [
              expandOptions &&
                expandOptions.fixed !== 'right' &&
                renderExpandTd(row, rowExpanded),
              tableTrContent,
              expandOptions &&
                expandOptions.fixed === 'right' &&
                renderExpandTd(row, rowExpanded)
            ]
          )
        );

        if (rowExpanded) {
          tableBodyContent.push(
            h(
              'tr',
              {
                class: trBem()
              },
              [
                h(
                  'td',
                  {
                    key:
                      'tr-expand-' +
                      (row[_this3.dataPropsMapping.id] !== undefined
                        ? row[_this3.dataPropsMapping.id]
                        : rowIndex),
                    class: tdBem(),
                    attrs: {
                      colSpan: columns.length + (expandOptions ? 1 : 0)
                    }
                  },
                  [
                    expandOptions.renderContent(_this3.$createElement, {
                      rowData: row
                    })
                  ]
                )
              ]
            )
          );
        }
      });
      var tableFakeTr = h(
        'tr',
        {
          ref: 'tableFakeTr',
          key: 'fake-column',
          class: trBem()
        },
        [
          expandOptions &&
            expandOptions.fixed !== 'right' &&
            h('td', {
              key: 'expand-former',
              class: tdBem([
                {
                  fake: true
                }
              ])
            }),
          columns.map(function (column) {
            return h('td', {
              key: column.dataProp,
              class: tdBem([
                {
                  fake: true
                }
              ])
            });
          }),
          expandOptions &&
            expandOptions.fixed === 'right' &&
            h('td', {
              key: 'expand-latter',
              class: tdBem([
                {
                  fake: true
                }
              ])
            })
        ]
      );
      return h(
        'tbody',
        {
          class: tbodyBem()
        },
        [tableBodyContent, tableFakeTr]
      );
    },
    TableEmtpyBody: function TableEmtpyBody() {
      var h = this.$createElement;
      var data = this.data,
        emptyContentImage = this.emptyContentImage,
        emptyContentText = this.emptyContentText;
      if (data.length) return null;
      return h(
        'div',
        {
          class: tbodyBem(['empty'])
        },
        [
          h(EmptyContent, {
            attrs: {
              emptyContentImage: emptyContentImage,
              emptyContentText: emptyContentText
            }
          })
        ]
      );
    },
    TableFooter: function TableFooter() {
      var h = this.$createElement;
      return h('tfoot', {
        class: tfootBem()
      });
    },
    TalbeSwipeableHeader: function TalbeSwipeableHeader() {
      var h = this.$createElement;
      var indicatorValue = this.indicatorValue,
        columns = this.columns,
        swipeable = this.swipeable,
        showIndicator = this.showIndicator;
      return (
        swipeable &&
        showIndicator &&
        h(
          'div',
          {
            class: bem('indicator')
          },
          [
            h(TableIndicator, {
              attrs: {
                value: indicatorValue,
                length: columns.length
              }
            })
          ]
        )
      );
    }
  }
});
